import { useEffect, useMemo, useState } from "react";
import { usePool } from "@/components/layout/AppShell";
import { useCurrentWeek } from "@/features/pools/queries";
import { CommissionerDataUnavailable } from "./api";
import { useCommissionerDashboard } from "./queries";

/**
 * Side Action Picks — commissioner dashboard.
 *
 * Drop in at web/src/pages/CommissionerDashboard.tsx.
 *
 * NOTE ON DATA: this is built against the explicit `DashboardData` interface below,
 * with mock values, deliberately. I don't have the live schema in front of me and
 * guessing column names is how the last round of bugs happened. Wire the query to
 * produce this shape and the UI needs no changes.
 */

const PAPER = "#F2F3F0";
const PAPER_DEEP = "#E8EAE5";
const INK = "#14181C";
const RULE = "#D3D7D0";
const FIELD = "#17603A";
const ALERT = "#C8461E";
const MUTED = "#6B7178";

type MemberStatus = {
  id: string;
  name: string;
  picksMade: number;
  lockSet: boolean;
  rulesAcked: boolean;
  balance: number; // positive = owed to the pool
};

export type DashboardData = {
  poolName: string;
  weekLabel: string;
  gamesThisWeek: number;
  locksAt: string; // ISO timestamp
  memberLimit: number;
  inviteUrl: string;
  members: MemberStatus[];
  standings: { name: string; points: number; weeksWon: number }[];
};

const MOCK: DashboardData = {
  poolName: "Preseason Finale",
  weekLabel: "Week 4",
  gamesThisWeek: 16,
  locksAt: new Date(Date.now() + 6 * 3600 * 1000 + 12 * 60 * 1000).toISOString(),
  memberLimit: 12,
  inviteUrl: "https://app.sideactionpicks.com/join/8FK2QA",
  members: [
    { id: "1", name: "Brian Ford", picksMade: 16, lockSet: true, rulesAcked: true, balance: 0 },
    { id: "2", name: "Nick", picksMade: 16, lockSet: true, rulesAcked: true, balance: 20 },
    { id: "3", name: "Paul", picksMade: 16, lockSet: true, rulesAcked: true, balance: 0 },
    { id: "4", name: "Peter", picksMade: 16, lockSet: true, rulesAcked: true, balance: -15 },
    { id: "5", name: "Robert", picksMade: 16, lockSet: true, rulesAcked: false, balance: 20 },
    { id: "6", name: "Bridget", picksMade: 0, lockSet: false, rulesAcked: true, balance: 20 },
    { id: "7", name: "Denise", picksMade: 0, lockSet: false, rulesAcked: false, balance: 20 },
    { id: "8", name: "Drew", picksMade: 11, lockSet: false, rulesAcked: true, balance: 0 },
    { id: "9", name: "Jarrett", picksMade: 0, lockSet: false, rulesAcked: true, balance: 20 },
    { id: "10", name: "Terry", picksMade: 0, lockSet: false, rulesAcked: true, balance: 20 },
  ],
  standings: [
    { name: "Peter", points: 41, weeksWon: 2 },
    { name: "Nick", points: 39, weeksWon: 1 },
    { name: "Brian Ford", points: 37, weeksWon: 0 },
    { name: "Paul", points: 34, weeksWon: 1 },
    { name: "Robert", points: 31, weeksWon: 0 },
  ],
};

function useCountdown(iso: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(iso).getTime() - now;
  const locked = ms <= 0;
  const h = Math.max(0, Math.floor(ms / 3600000));
  const m = Math.max(0, Math.floor((ms % 3600000) / 60000));
  const s = Math.max(0, Math.floor((ms % 60000) / 1000));
  return { locked, h, m, s };
}

export default function CommissionerDashboard({ data = MOCK }: { data?: DashboardData }) {
  const [copied, setCopied] = useState<"none" | "reminder" | "invite">("none");
  const { locked, h, m, s } = useCountdown(data.locksAt);

  const incomplete = useMemo(
    () => data.members.filter((x) => x.picksMade < data.gamesThisWeek),
    [data]
  );
  const complete = data.members.length - incomplete.length;
  const unacked = data.members.filter((x) => !x.rulesAcked);
  const owed = data.members.reduce((a, b) => a + Math.max(0, b.balance), 0);

  const reminderText = `${data.weekLabel} picks lock in ${h}h ${m}m. Still need: ${incomplete
    .map((x) => x.name)
    .join(", ")}.`;

  async function copy(text: string, which: "reminder" | "invite") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied("none"), 2000);
    } catch {
      setCopied("none");
    }
  }

  return (
    <div
      className="min-h-screen antialiased"
      style={{ background: PAPER, color: INK, fontFamily: "Archivo, system-ui, sans-serif" }}
    >
      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="mx-auto flex max-w-5xl items-baseline justify-between px-5 py-4">
          <div className="flex items-baseline gap-3">
            <span className="text-[15px] font-extrabold tracking-tight">{data.poolName}</span>
            <span className="text-[13px]" style={{ color: MUTED }}>
              Commissioner
            </span>
          </div>
          <a href="/pools" className="text-[13px] font-medium hover:underline">
            All pools
          </a>
        </div>
      </header>

      {/* ---------- lock status: the whole point of the page ---------- */}
      <section
        className="border-b"
        style={{ borderColor: INK, background: incomplete.length ? INK : PAPER_DEEP }}
      >
        <div className="mx-auto max-w-5xl px-5 py-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p
                className="text-[13px] font-semibold"
                style={{ color: incomplete.length ? "#A9B0B6" : MUTED }}
              >
                {data.weekLabel} · {data.gamesThisWeek} games
              </p>
              <p
                className="mt-2 text-[2.6rem] font-extrabold leading-none tabular-nums tracking-tight"
                style={{ color: incomplete.length ? "#FFFFFF" : INK }}
              >
                {locked ? "Picks are locked" : `${h}h ${m}m ${String(s).padStart(2, "0")}s`}
              </p>
              <p
                className="mt-2 text-[15px]"
                style={{ color: incomplete.length ? "#A9B0B6" : MUTED }}
              >
                {locked ? "No further changes" : "until picks lock"}
              </p>
            </div>

            <div className="text-right">
              <p
                className="text-[2.6rem] font-extrabold leading-none tabular-nums tracking-tight"
                style={{ color: incomplete.length ? "#FFFFFF" : INK }}
              >
                {complete}/{data.members.length}
              </p>
              <p
                className="mt-2 text-[15px]"
                style={{ color: incomplete.length ? "#A9B0B6" : MUTED }}
              >
                slates complete
              </p>
            </div>
          </div>

          {incomplete.length > 0 && (
            <div className="mt-7 border-t pt-6" style={{ borderColor: "#2C333A" }}>
              <p className="text-[15px] font-bold text-white">
                {incomplete.length} {incomplete.length === 1 ? "person hasn't" : "people haven't"}{" "}
                finished
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {incomplete.map((x) => (
                  <span
                    key={x.id}
                    className="rounded-sm border px-2.5 py-1.5 text-[13px] font-semibold"
                    style={{ borderColor: ALERT, color: "#FFFFFF" }}
                  >
                    {x.name}
                    {x.picksMade > 0 && (
                      <span className="ml-1.5 tabular-nums" style={{ color: "#A9B0B6" }}>
                        {x.picksMade}/{data.gamesThisWeek}
                      </span>
                    )}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => copy(reminderText, "reminder")}
                className="mt-5 rounded-sm px-5 py-3 text-[14px] font-semibold text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ background: copied === "reminder" ? FIELD : ALERT }}
              >
                {copied === "reminder" ? "Copied — paste it in the group text" : "Copy reminder text"}
              </button>
              <p className="mt-3 max-w-[60ch] text-[13px]" style={{ color: "#7C848B" }}>
                {reminderText}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------- member grid ---------- */}
      <section className="mx-auto max-w-5xl px-5 py-12">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[1.4rem] font-extrabold tracking-[-0.02em]">This week</h2>
          <span className="text-[13px] tabular-nums" style={{ color: MUTED }}>
            {data.members.length} of {data.memberLimit} members
          </span>
        </div>

        <table className="mt-5 w-full border-collapse text-left">
          <thead>
            <tr className="border-b" style={{ borderColor: INK }}>
              <th className="pb-2 text-[13px] font-bold">Member</th>
              <th className="pb-2 text-right text-[13px] font-bold">Picks</th>
              <th className="pb-2 text-right text-[13px] font-bold">Lock</th>
              <th className="pb-2 text-right text-[13px] font-bold">Rules</th>
              <th className="pb-2 text-right text-[13px] font-bold">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((x) => {
              const done = x.picksMade >= data.gamesThisWeek;
              return (
                <tr key={x.id} className="border-b" style={{ borderColor: RULE }}>
                  <td className="py-3 text-[15px] font-semibold">{x.name}</td>
                  <td
                    className="py-3 text-right text-[15px] font-semibold tabular-nums"
                    style={{ color: done ? INK : ALERT }}
                  >
                    {x.picksMade}/{data.gamesThisWeek}
                  </td>
                  <td className="py-3 text-right text-[15px] tabular-nums" style={{ color: MUTED }}>
                    {x.lockSet ? "set" : "—"}
                  </td>
                  <td
                    className="py-3 text-right text-[15px]"
                    style={{ color: x.rulesAcked ? MUTED : ALERT }}
                  >
                    {x.rulesAcked ? "ok" : "not read"}
                  </td>
                  <td className="py-3 text-right text-[15px] tabular-nums" style={{ color: MUTED }}>
                    {x.balance === 0 ? "—" : `$${Math.abs(x.balance)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {unacked.length > 0 && (
          <p className="mt-4 text-[13px]" style={{ color: MUTED }}>
            {unacked.length} {unacked.length === 1 ? "member hasn't" : "members haven't"} read the
            current rules. They can still pick — this is informational.
          </p>
        )}
      </section>

      {/* ---------- standings + ledger ---------- */}
      <section className="border-t" style={{ borderColor: RULE, background: PAPER_DEEP }}>
        <div className="mx-auto grid max-w-5xl gap-10 px-5 py-12 md:grid-cols-2">
          <div>
            <h2 className="text-[1.4rem] font-extrabold tracking-[-0.02em]">Standings</h2>
            <ol className="mt-5">
              {data.standings.map((r, i) => (
                <li
                  key={r.name}
                  className="flex items-baseline justify-between border-b py-2.5"
                  style={{ borderColor: RULE }}
                >
                  <span className="text-[15px] font-semibold">
                    <span className="mr-3 tabular-nums" style={{ color: MUTED }}>
                      {i + 1}
                    </span>
                    {r.name}
                  </span>
                  <span className="text-[15px] tabular-nums" style={{ color: MUTED }}>
                    {r.points} pts
                    {r.weeksWon > 0 && (
                      <span className="ml-3" style={{ color: FIELD }}>
                        {r.weeksWon}W
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="text-[1.4rem] font-extrabold tracking-[-0.02em]">Ledger</h2>
            <p className="mt-4 text-[2rem] font-extrabold leading-none tabular-nums">${owed}</p>
            <p className="mt-2 text-[15px]" style={{ color: MUTED }}>
              outstanding across {data.members.filter((x) => x.balance > 0).length} members
            </p>
            <p className="mt-4 max-w-[46ch] text-[14px] leading-relaxed" style={{ color: MUTED }}>
              Side Action doesn't move money. This is a tally so you know who to chase — settle it
              however your group already does.
            </p>
            <a href="/ledger" className="mt-4 inline-block text-[14px] font-semibold underline underline-offset-4">
              See the full ledger
            </a>
          </div>
        </div>
      </section>

      {/* ---------- invite ---------- */}
      <section className="mx-auto max-w-5xl px-5 py-12">
        <h2 className="text-[1.4rem] font-extrabold tracking-[-0.02em]">Add people</h2>
        <p className="mt-3 max-w-[54ch] text-[15px] leading-relaxed" style={{ color: MUTED }}>
          Anyone with this link can join. Members added after a week locks won't appear in that
          week's results.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <code
            className="rounded-sm border px-3 py-2.5 text-[13px]"
            style={{ borderColor: RULE, background: "#FFFFFF" }}
          >
            {data.inviteUrl}
          </code>
          <button
            type="button"
            onClick={() => copy(data.inviteUrl, "invite")}
            className="rounded-sm px-5 py-2.5 text-[14px] font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ background: FIELD }}
          >
            {copied === "invite" ? "Copied" : "Copy invite link"}
          </button>
        </div>
        <p className="mt-3 text-[13px]" style={{ color: MUTED }}>
          {data.memberLimit - data.members.length} spots left on your free pool.
        </p>
      </section>
    </div>
  );
}


/* ---------------------------------------------------------------------------
 * Connected page
 *
 * The default export above stays presentational with its MOCK default, so
 * /dash-preview keeps rendering with no session and no database behind it.
 * This wrapper is the one that talks to Supabase.
 *
 * The week comes from useCurrentWeek -- the same "earliest week that has not
 * locked yet" the picks page uses. Resolving the current week a second way here
 * would drift from the week members are actually picking. fetchDashboardData
 * then re-resolves it by (season_id, week_number) rather than trusting the id it
 * was handed, which is what keeps the query pinned to an explicit week.
 *
 * Note this reads pool context from RequirePool, not AppShell: the route sits
 * outside AppShell because this is a max-w-5xl page carrying its own header,
 * and AppShell wraps its children in max-w-md under a tab bar.
 * ------------------------------------------------------------------------- */

function errorMessage(error: unknown) {
  if (error instanceof CommissionerDataUnavailable) return error.message;

  /* 42501 is what commissioner_week_roster (0011) raises for a caller who is not
     the pool's commissioner. PostgREST passes the SQLSTATE through as `code`, so
     the one case a member can legitimately hit gets a sentence rather than a
     raw Postgres string. */
  if (typeof error === "object" && error !== null && "code" in error) {
    if ((error as { code: unknown }).code === "42501") {
      return "This dashboard is for the pool's commissioner.";
    }
  }

  return error instanceof Error ? error.message : "Something went wrong.";
}

export function CommissionerDashboardLive() {
  const pool = usePool();
  const { data: week, isPending: weekPending, error: weekError } = useCurrentWeek(pool.season_id);
  const { data, isPending, error } = useCommissionerDashboard(pool.id, week?.week_number);

  const failure = weekError ?? error;
  if (failure) return <p className="eyebrow px-6 py-10">{errorMessage(failure)}</p>;

  /* isPending stays true while the query is disabled, which it is until the week
     resolves -- hence the explicit week check rather than relying on it alone. */
  if (weekPending || isPending || !data) {
    return <p className="eyebrow px-6 py-10">Loading the dashboard…</p>;
  }

  return <CommissionerDashboard data={data} />;
}
